import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Upload } from 'antd';
import { useNavigate, useParams } from 'react-router';
import { getUserById } from '../../redux/actions/user/getUserById';
import { useDispatch, useSelector } from 'react-redux';
import Input from '../../components/Input/Input';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { resetPasswordUser } from '../../redux/actions/user/resetPasswordUser';
import {
  resetGetUserById,
  resetResetPasswordUser,
  resetUpdateEmailUser,
  resetUpdateUser,
  resetDeleteUserList,
  resetGetUserPermissions,
  resetUpdateUserPermissions,
  resetUpdateUserMaxEmployers,
  resetUpdateUserAutoEmployers,
  resetAddUserEmployers,
  resetRemoveUserEmployers,
  resetTransferUserEmployers,
  resetGetUserEmployersDistribution,
} from '../../redux/slices/user.slice';
import { updateEmailUser } from '../../redux/actions/user/updateEmailUser';
import { updateUser } from '../../redux/actions/user/updateUser';
import Loading from '../../components/Loading/Loading';
import PermissionsCheckboxList from '../../components/Permissions/PermissionsCheckboxList';
import { getUserPermissions } from '../../redux/actions/user/getUserPermissions';
import { updateUserPermissions } from '../../redux/actions/user/updateUserPermissions';
import { resolvePermissionForApi } from '../../constants/permissions';
import { updateUserMaxEmployers } from '../../redux/actions/user/updateUserMaxEmployers';
import { updateUserAutoEmployers } from '../../redux/actions/user/updateUserAutoEmployers';
import { toast } from 'react-toastify';
import { getMe } from '../../redux/actions/user/getMe';
import SelectionPerformer from '../SelectionPerformer';
import { addUserEmployers } from '../../redux/actions/user/addUserEmployers';
import { removeUserEmployers } from '../../redux/actions/user/removeUserEmployers';
import { transferUserEmployers } from '../../redux/actions/user/transferUserEmployers';
import { getUserEmployersDistribution } from '../../redux/actions/user/getUserEmployersDistribution';
import { deleteUserList } from '../../redux/actions/user/deleteUserList';
import { axiosInstance } from '../../utils/axiosInterceptor';
import EmployersDistributionView from '../../components/EmployersDistribution/EmployersDistributionView';

const EMPLOYERS_LIMIT_PERMISSION = 'change_user_employers';
const MAX_EMPLOYERS_LIMIT = 2147483647;
const BROADCAST_MODERATION_PERMISSION = 'change_vacancy_broadcast_for_moderation_action';
const ACCEPT_MODERATION_PERMISSION = 'accept_moderation';

const toIntegerOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) {
    return null;
  }

  return numericValue;
};

const formatQuotaValue = (value) => (Number.isInteger(value) ? value : '—');

function EditUser() {
  const [fileList, setFileList] = useState([]);
  const [totalEditState, setTotalEditState] = useState(false);
  const [permissionsEditState, setPermissionsEditState] = useState(false);
  const [pendingQuotaUpdate, setPendingQuotaUpdate] = useState(null);
  const [isFreeEmployersModalOpen, setIsFreeEmployersModalOpen] = useState(false);
  const [isAttachedEmployersModalOpen, setIsAttachedEmployersModalOpen] = useState(false);
  const [isDistributionModalOpen, setIsDistributionModalOpen] = useState(false);
  const [freeEmployersCount, setFreeEmployersCount] = useState(0);
  const [attachedEmployersCount, setAttachedEmployersCount] = useState(0);
  const [transferActionType, setTransferActionType] = useState(null);

  const form = useForm({
    defaultValues: {
      email: '',
      fio: '',
      permissions: {},
      quota: '',
      auto_employers: false,
    },
  });

  const navigate = useNavigate();
  const { userId } = useParams();
  const dispatch = useDispatch();

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const onPreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const {
    getMe: { data: getMeData },
    getUserById: { data: getUserByIdData, error: getUserByIdError, loading: getUserByIdLoading },
    resetPasswordUser: { data: resetPasswordUserData, error: resetPasswordUserError, loading: resetPasswordUserLoading },
    updateUser: { data: updateUserData, error: updateUserError, loading: updateUserLoading },
    updateEmailUser: { data: updateEmailUserData, error: updateEmailUserError, loading: updateEmailUserLoading },
    deleteUserList: { data: deleteUserListData, error: deleteUserListError, loading: deleteUserListLoading },
    getUserPermissions: { data: userPermissionsData, loading: userPermLoading },
    updateUserPermissions: { data: updatePermData, error: updatePermError, loading: updatePermLoading },
    updateUserMaxEmployers: { data: updateMaxEmployersData, error: updateMaxEmployersError, loading: updateMaxEmployersLoading },
    updateUserAutoEmployers: { error: updateAutoEmployersError, loading: updateAutoEmployersLoading },
    addUserEmployers: { data: addUserEmployersData, error: addUserEmployersError, loading: addUserEmployersLoading },
    removeUserEmployers: { data: removeUserEmployersData, error: removeUserEmployersError, loading: removeUserEmployersLoading },
    transferUserEmployers: { data: transferUserEmployersData, error: transferUserEmployersError, loading: transferUserEmployersLoading },
    getUserEmployersDistribution: { data: employersDistributionData, loading: employersDistributionLoading, error: employersDistributionError },
  } = useSelector((state) => state.user);
  const isSuperuser = getMeData?.is_superuser === true;
  const currentUserId = getMeData?.id;
  const isSuperAdminAdmin = isSuperuser && getMeData?.role === 'admin';

  const fetchEmployersCounts = useCallback(async () => {
    if (!userId) {
      return;
    }

    const freeParams = isSuperAdminAdmin ? { responsible_is_null: true, has_access: false, page: 1 } : { responsible_id: currentUserId, page: 1 };
    const attachedParams = { responsible_id: userId, page: 1 };

    if (!isSuperAdminAdmin && !currentUserId) {
      setFreeEmployersCount(0);
      setAttachedEmployersCount(0);
      return;
    }

    try {
      const [freeEmployersResponse, attachedEmployersResponse] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_SERVER_API}/employers/`, {
          params: freeParams,
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        }),
        axiosInstance.get(`${process.env.REACT_APP_SERVER_API}/employers/`, {
          params: attachedParams,
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        }),
      ]);

      setFreeEmployersCount(Number(freeEmployersResponse?.data?.count) || 0);
      setAttachedEmployersCount(Number(attachedEmployersResponse?.data?.count) || 0);
    } catch (error) {
      setFreeEmployersCount(0);
      setAttachedEmployersCount(0);
    }
  }, [userId, isSuperAdminAdmin, currentUserId]);

  // Загружаем данные пользователя и его полномочия
  useEffect(() => {
    dispatch(resetUpdateUserPermissions());
    dispatch(resetUpdateUserMaxEmployers());
    dispatch(resetUpdateUserAutoEmployers());
    dispatch(resetAddUserEmployers());
    dispatch(resetRemoveUserEmployers());
    dispatch(resetTransferUserEmployers());
    dispatch(resetGetUserEmployersDistribution());
    dispatch(resetDeleteUserList());
    dispatch(getMe());
    dispatch(getUserById(userId));
    dispatch(getUserEmployersDistribution(userId));
    dispatch(getUserPermissions(userId));

    return () => {
      form.reset();
      dispatch(resetGetUserById());
      dispatch(resetGetUserPermissions());
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
      dispatch(resetUpdateUserAutoEmployers());
      dispatch(resetAddUserEmployers());
      dispatch(resetRemoveUserEmployers());
      dispatch(resetTransferUserEmployers());
      dispatch(resetGetUserEmployersDistribution());
      dispatch(resetDeleteUserList());
    };
  }, [userId]);

  // Заполняем форму данными пользователя
  useEffect(() => {
    if (getUserByIdData) {
      form.setValue('email', getUserByIdData?.email);
      form.setValue('fio', getUserByIdData?.full_name);
      form.setValue('quota', toIntegerOrNull(getUserByIdData?.max_employers) ?? '');
      form.setValue('auto_employers', Boolean(getUserByIdData?.auto_employers));
    }
  }, [getUserByIdData]);

  useEffect(() => {
    fetchEmployersCounts();
  }, [fetchEmployersCounts]);

  // Обработка успешного обновления данных
  useEffect(() => {
    if (updateUserData && updateEmailUserData) {
      Swal.fire({
        title: 'Данные пользователя успешно изменены',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetUpdateEmailUser());
      dispatch(resetUpdateUser());
      dispatch(getUserById(userId));
      setTotalEditState(false);
    }
  }, [updateUserData, updateEmailUserData]);

  // Обработка успешного обновления полномочий
  useEffect(() => {
    if (updatePermData) {
      if (pendingQuotaUpdate?.shouldUpdateQuota) {
        dispatch(updateUserMaxEmployers({ userId, max_employers: pendingQuotaUpdate.maxEmployers }));
        return;
      }

      Swal.fire({
        title: 'Полномочия пользователя успешно обновлены',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
      dispatch(getMe());
      dispatch(getUserPermissions(userId));
      dispatch(getUserById(userId));
      dispatch(getUserEmployersDistribution(userId));
      fetchEmployersCounts();
      setPendingQuotaUpdate(null);
      setPermissionsEditState(false);
    }
  }, [updatePermData, pendingQuotaUpdate, dispatch, userId, fetchEmployersCounts]);

  useEffect(() => {
    if (updateMaxEmployersData) {
      Swal.fire({
        title: 'Полномочия и лимит пользователя успешно обновлены',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
      dispatch(getMe());
      dispatch(getUserPermissions(userId));
      dispatch(getUserById(userId));
      dispatch(getUserEmployersDistribution(userId));
      fetchEmployersCounts();
      setPendingQuotaUpdate(null);
      setPermissionsEditState(false);
    }
  }, [updateMaxEmployersData, dispatch, userId, fetchEmployersCounts]);

  useEffect(() => {
    if (updatePermError) {
      toast.error('Не удалось сохранить полномочия пользователя', { theme: 'colored', autoClose: '3000' });
      dispatch(resetUpdateUserPermissions());
      setPendingQuotaUpdate(null);
    }
  }, [updatePermError, dispatch]);

  useEffect(() => {
    if (updateMaxEmployersError) {
      toast.error('Полномочия сохранены, но не удалось сохранить лимит контрагентов', { theme: 'colored', autoClose: '3000' });
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
      setPendingQuotaUpdate(null);
      dispatch(getUserById(userId));
    }
  }, [updateMaxEmployersError, dispatch, userId]);

  useEffect(() => {
    if (updateAutoEmployersError) {
      toast.error('Не удалось обновить автораспределение контрагентов', { theme: 'colored', autoClose: '3000' });
      dispatch(resetUpdateUserAutoEmployers());
    }
  }, [updateAutoEmployersError, dispatch]);

  useEffect(() => {
    if (addUserEmployersData !== null) {
      toast.success('Контрагенты успешно прикреплены', { theme: 'colored', autoClose: '3000' });
      setIsFreeEmployersModalOpen(false);
      dispatch(resetAddUserEmployers());
      dispatch(getUserById(userId));
      dispatch(getUserPermissions(userId));
      dispatch(getUserEmployersDistribution(userId));
      fetchEmployersCounts();
    }
  }, [addUserEmployersData, dispatch, userId, fetchEmployersCounts]);

  useEffect(() => {
    if (removeUserEmployersData === null || !isAttachedEmployersModalOpen) {
      return;
    }

    toast.success('Контрагенты успешно откреплены', { theme: 'colored', autoClose: '3000' });
    setIsAttachedEmployersModalOpen(false);
    dispatch(resetRemoveUserEmployers());
    dispatch(getUserById(userId));
    dispatch(getUserPermissions(userId));
    dispatch(getUserEmployersDistribution(userId));
    fetchEmployersCounts();
  }, [removeUserEmployersData, isAttachedEmployersModalOpen, dispatch, userId, fetchEmployersCounts]);

  useEffect(() => {
    if (addUserEmployersError) {
      toast.error('Не удалось прикрепить контрагентов', { theme: 'colored', autoClose: '3000' });
      dispatch(resetAddUserEmployers());
    }
  }, [addUserEmployersError, dispatch]);

  useEffect(() => {
    if (!isAttachedEmployersModalOpen || !removeUserEmployersError) {
      return;
    }

    toast.error('Не удалось открепить контрагентов', { theme: 'colored', autoClose: '3000' });
    dispatch(resetRemoveUserEmployers());
  }, [removeUserEmployersError, isAttachedEmployersModalOpen, dispatch]);

  useEffect(() => {
    if (transferUserEmployersData === null || !transferActionType) {
      return;
    }

    if (transferActionType === 'attach') {
      toast.success('Контрагенты успешно прикреплены', { theme: 'colored', autoClose: '3000' });
      setIsFreeEmployersModalOpen(false);
    } else if (transferActionType === 'detach') {
      toast.success('Контрагенты успешно откреплены', { theme: 'colored', autoClose: '3000' });
      setIsAttachedEmployersModalOpen(false);
    }

    dispatch(resetTransferUserEmployers());
    dispatch(getUserById(userId));
    dispatch(getUserPermissions(userId));
    dispatch(getUserEmployersDistribution(userId));
    fetchEmployersCounts();
    setTransferActionType(null);
  }, [transferUserEmployersData, transferActionType, dispatch, userId, fetchEmployersCounts]);

  useEffect(() => {
    if (!transferActionType || !transferUserEmployersError) {
      return;
    }

    toast.error('Не удалось перенести контрагентов', { theme: 'colored', autoClose: '3000' });
    dispatch(resetTransferUserEmployers());
    setTransferActionType(null);
  }, [transferUserEmployersError, transferActionType, dispatch]);

  useEffect(() => {
    if (deleteUserListData) {
      toast.success('Пользователь успешно удален', { theme: 'colored', autoClose: '3000' });
      dispatch(resetDeleteUserList());
      navigate('/manage');
    }
  }, [deleteUserListData, dispatch, navigate]);

  useEffect(() => {
    if (deleteUserListError) {
      toast.error('Не удалось удалить пользователя', { theme: 'colored', autoClose: '3000' });
      dispatch(resetDeleteUserList());
    }
  }, [deleteUserListError, dispatch]);

  // Сброс пароля
  const handleResetPassword = () => {
    Swal.fire({
      title: 'Вы действительно хотите сбросить пароль пользователя?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(resetPasswordUser(userId));
      }
    });
  };

  const handleDeleteUser = () => {
    Swal.fire({
      title: 'Вы действительно хотите удалить пользователя?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteUserList({
            users: [Number(userId)],
            is_active: false,
          }),
        );
      }
    });
  };

  useEffect(() => {
    if (resetPasswordUserData) {
      Swal.fire({
        title: 'Новый пароль отправлен на почту',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetResetPasswordUser());
    }
  }, [resetPasswordUserData]);

  // Сохранение основных данных
  const onSubmit = (data) => {
    Swal.fire({
      title: 'Вы действительно хотите изменить данные пользователя?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(updateEmailUser({ id: userId, email: data.email }));
        dispatch(updateUser({ id: userId, fullName: data.fio, phone: getUserByIdData?.phone }));
      }
    });
  };

  // Сохранение полномочий
  const onSavePermissions = (data) => {
    const permissionsData = form.getValues('permissions');
    const permissionNames = new Set(
      Object.entries(permissionsData)
        .filter(([_, checked]) => checked)
        .map(([name]) => resolvePermissionForApi(name)),
    );

    if (permissionsData[BROADCAST_MODERATION_PERMISSION]) {
      permissionNames.add(ACCEPT_MODERATION_PERMISSION);
    }

    const requestedQuota = toIntegerOrNull(data.quota);
    const hasEmployersLimitPermission = permissionNames.has(EMPLOYERS_LIMIT_PERMISSION);
    const canUpdateQuota = hasEmployersLimitPermission && canManageTargetQuotaByHierarchy;

    if (canUpdateQuota && isQuotaBlockedByMissingData) {
      toast.error('Не удалось загрузить исходный лимит. Сохранение лимита временно недоступно.', {
        theme: 'colored',
        autoClose: '3000',
      });
      return;
    }

    Swal.fire({
      title: 'Вы действительно хотите изменить полномочия пользователя?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        permissionNames.add('view_user');
        if (permissionNames.has('add_manager')) {
          permissionNames.add('add_user');
          permissionNames.add('change_user_permissions');
          permissionNames.add('view_permissions');
        }

        const shouldUpdateQuota = Boolean(canUpdateQuota && Number.isInteger(requestedQuota) && (isSuperuser || (Number.isInteger(targetOriginalQuota) && requestedQuota !== targetOriginalQuota)));
        setPendingQuotaUpdate({
          shouldUpdateQuota,
          maxEmployers: requestedQuota ?? 0,
        });

        dispatch(
          updateUserAutoEmployers({
            userId,
            auto_employers: Boolean(data.auto_employers),
          }),
        );

        const permissionsArray = Array.from(permissionNames).map((name) => ({ name }));
        dispatch(updateUserPermissions({ userId, permissions: permissionsArray }));
      }
    });
  };

  const selectedPermissions = form.watch('permissions') || {};
  const rawEditedTargetQuota = form.watch('quota');
  const editedTargetQuota = toIntegerOrNull(rawEditedTargetQuota);
  const editedTargetQuotaNumeric = rawEditedTargetQuota === '' || rawEditedTargetQuota === null || rawEditedTargetQuota === undefined ? null : Number(rawEditedTargetQuota);
  const isEmployersLimitEnabled = Boolean(selectedPermissions[EMPLOYERS_LIMIT_PERMISSION]);
  const ownOriginalQuota = toIntegerOrNull(getMeData?.original_quota);
  const ownAvailableQuota = toIntegerOrNull(getMeData?.max_employers);
  const targetOriginalQuota = toIntegerOrNull(getUserByIdData?.original_quota);
  const targetAvailableQuota = toIntegerOrNull(getUserByIdData?.max_employers);
  const targetCreatorId = getUserByIdData?.creator?.id;
  const isOwnSubordinate = targetCreatorId !== undefined && targetCreatorId !== null && currentUserId !== undefined && currentUserId !== null && Number(targetCreatorId) === Number(currentUserId);
  const hideEmployersLimitsPermissionOption = !isSuperuser && getUserByIdData && !isOwnSubordinate;
  const canManageTargetQuotaByHierarchy = isSuperuser || isOwnSubordinate;
  const showQuotaBlock = isEmployersLimitEnabled && canManageTargetQuotaByHierarchy;
  const isEmployersActionsBlockedByMeData = !isSuperAdminAdmin && !currentUserId;
  const targetQuotaPreview = Number.isFinite(editedTargetQuotaNumeric) ? editedTargetQuotaNumeric : (editedTargetQuota ?? targetOriginalQuota ?? 0);
  const quotaDelta = Number.isInteger(targetOriginalQuota) ? targetQuotaPreview - targetOriginalQuota : 0;
  const ownAvailablePreview = Number.isInteger(ownAvailableQuota) ? ownAvailableQuota - quotaDelta : null;
  const targetAvailablePreview = Number.isInteger(targetAvailableQuota) ? targetAvailableQuota + quotaDelta : null;
  const editableQuotaLimit = isSuperuser ? MAX_EMPLOYERS_LIMIT : (targetOriginalQuota ?? 0) + (ownAvailableQuota ?? 0);
  const isQuotaExceeded = !isSuperuser && Number.isFinite(editedTargetQuotaNumeric) && editedTargetQuotaNumeric > editableQuotaLimit;
  const isQuotaBlockedByMissingData = !isSuperuser && (!Number.isInteger(ownOriginalQuota) || !Number.isInteger(targetOriginalQuota) || !Number.isInteger(ownAvailableQuota));
  const ownAssignedEmployers = Number.isFinite(Number(getMeData?.assigned_employers)) ? Number(getMeData?.assigned_employers) : 0;
  const ownDistributedEmployers = Number.isFinite(Number(getMeData?.distributed_employers)) ? Number(getMeData?.distributed_employers) : 0;
  const ownContractorsTotal = ownAssignedEmployers + ownDistributedEmployers;
  const ownContractorsAvailable = ownAssignedEmployers;
  const isCurrentUserSuperuser = getMeData?.is_superuser === true;
  const isTargetUserSuperuser = getUserByIdData?.is_superuser === true;
  const isTargetUserCreatedBySuperuser = getUserByIdData?.creator?.is_superuser === true;
  const targetAssignedEmployers = Number.isFinite(Number(getUserByIdData?.assigned_employers)) ? Number(getUserByIdData?.assigned_employers) : 0;
  const targetDistributedEmployers = Number.isFinite(Number(getUserByIdData?.distributed_employers)) ? Number(getUserByIdData?.distributed_employers) : 0;
  const targetContractorsTotal = targetAssignedEmployers + targetDistributedEmployers;
  const targetContractorsAvailable = targetAssignedEmployers;
  const ownLimitsTotalDisplay = toIntegerOrNull(getMeData?.max_employers) ?? ownOriginalQuota;
  const ownLimitsAvailableDisplay = toIntegerOrNull(getMeData?.available_employers) ?? ownAvailablePreview;
  const targetLimitsTotalDisplay = toIntegerOrNull(getUserByIdData?.max_employers) ?? targetQuotaPreview;
  const targetLimitsAvailableDisplay = toIntegerOrNull(getUserByIdData?.available_employers) ?? targetAvailablePreview;
  const ownContractorsTotalDisplay = toIntegerOrNull(getMeData?.total_employers) ?? ownContractorsTotal;
  const ownContractorsAvailableDisplay = toIntegerOrNull(getMeData?.available_employers) ?? ownContractorsAvailable;
  const ownContractorsFreeDisplay = toIntegerOrNull(getMeData?.free_employers) ?? ownContractorsAvailableDisplay;
  const targetContractorsTotalDisplay = toIntegerOrNull(getUserByIdData?.total_employers) ?? targetContractorsTotal;
  const targetContractorsAvailableDisplay = toIntegerOrNull(getUserByIdData?.available_employers) ?? targetContractorsAvailable;
  const isLoading = updateUserLoading || updateEmailUserLoading || resetPasswordUserLoading || updatePermLoading || updateMaxEmployersLoading || updateAutoEmployersLoading || addUserEmployersLoading || removeUserEmployersLoading || transferUserEmployersLoading || deleteUserListLoading;
  const isStaffUser = getUserByIdData?.type === 'staff';
  const quotaRules = {
    required: { value: showQuotaBlock, message: '*Обязательное поле для заполнения' },
    validate: (value) => {
      if (!showQuotaBlock) {
        return true;
      }

      if (isQuotaBlockedByMissingData) {
        return 'Не удалось загрузить исходный лимит для редактирования';
      }

      if (value === '' || value === null || value === undefined) {
        return 'Введите лимит контрагентов';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) {
        return 'Лимит должен быть целым числом';
      }

      if (numericValue < 0) {
        return 'Лимит не может быть отрицательным';
      }

      if (numericValue > MAX_EMPLOYERS_LIMIT) {
        return `Максимальное значение: ${MAX_EMPLOYERS_LIMIT}`;
      }

      if (!isSuperuser && numericValue > editableQuotaLimit) {
        return `Доступно не более ${editableQuotaLimit}`;
      }

      return true;
    },
  };
  const quotaRegister = form.register('quota', quotaRules);

  const handleQuotaBlur = () => {
    if (isSuperuser) {
      return;
    }

    const currentValue = Number(form.getValues('quota'));
    if (!Number.isFinite(currentValue)) {
      return;
    }

    if (currentValue > editableQuotaLimit) {
      form.setValue('quota', editableQuotaLimit, { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleSaveEmployers = (employers) => {
    if (!employers?.length) {
      return;
    }

    if (isSuperAdminAdmin) {
      dispatch(addUserEmployers({ userId, employers }));
      return;
    }

    if (!currentUserId) {
      toast.error('Не удалось определить текущего пользователя', { theme: 'colored', autoClose: '3000' });
      return;
    }

    setTransferActionType('attach');
    dispatch(
      transferUserEmployers({
        userId: Number(userId),
        from_user_id: Number(currentUserId),
        employers,
      }),
    );
  };

  const handleRemoveEmployers = (employers) => {
    if (!employers?.length) {
      return;
    }

    if (isTargetUserCreatedBySuperuser) {
      dispatch(removeUserEmployers({ userId, employers }));
      return;
    }

    if (!currentUserId) {
      toast.error('Не удалось определить текущего пользователя', { theme: 'colored', autoClose: '3000' });
      return;
    }

    setTransferActionType('detach');
    dispatch(
      transferUserEmployers({
        userId: Number(getUserByIdData?.creator?.id),
        from_user_id: Number(userId),
        employers,
      }),
    );
  };

  return (
    <div className="w-full flex flex-1 mt-10 p-1">
      <div className="container relative flex flex-1 flex-col mx-auto max-w-[1163px]">
        <div className="flex flex-col sm:flex-row w-full gap-3">
          <div className="flex w-full sm:w-[20%] mb-2 justify-center">
            {totalEditState ? (
              <div className="flex justify-center p-5">
                <Upload action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188" listType="picture-card" fileList={fileList} onChange={onChange} onPreview={onPreview}>
                  {fileList.length < 1 && 'Выгрузить'}
                </Upload>
              </div>
            ) : (
              <div className="bg-[#E5E5E5] w-[160px] h-[160px] m-5 flex items-center justify-center rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 24 24">
                  <path fill="rgba(0,0,0,0.6)" d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4" />
                </svg>
              </div>
            )}
          </div>

          <div className="flex flex-col w-full sm:w-[70%] mb-2 p-5 gap-3 relative">
            {isLoading && <Loading />}

            <div className="">{totalEditState ? <Input form={form} name="fio" placeholder="Имя" disabled={isLoading} /> : <label className="text-lg">{getUserByIdData?.full_name}</label>}</div>

            <div className="">
              <Input
                form={form}
                name="email"
                placeholder="Email"
                rules={{
                  required: { value: true, message: '*Обязательное поле для заполнения' },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Неверный формат почты',
                  },
                }}
                disabled={!totalEditState || isLoading}
              />
            </div>

            {totalEditState && (
              <button onClick={handleResetPassword} type="button" className="text-center text-[14px] border rounded py-2 px-4 text-white hover:bg-red-600 bg-red-500 w-[130px]" disabled={isLoading}>
                Сброс пароля
              </button>
            )}

            {!totalEditState ? (
              <>
                <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov w-full md:w-auto transition ease-in-out duration-300" onClick={() => setTotalEditState(true)}>
                  Редактировать
                </button>
                <button type="button" className="text-center border rounded py-2 px-4 text-white hover:bg-red-600 bg-red-500 w-full md:w-auto transition ease-in-out duration-300" onClick={handleDeleteUser} disabled={isLoading}>
                  Удалить
                </button>
                <button onClick={() => navigate(-1)} type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 w-full md:w-auto hover:text-white bg-white hover:bg-button transition ease-in-out duration-300">
                  Назад
                </button>
              </>
            ) : (
              <>
                <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov w-full md:w-auto transition ease-in-out duration-300" onClick={form.handleSubmit(onSubmit)} disabled={isLoading}>
                  Сохранить
                </button>
                <button type="button" className="text-center border rounded py-2 px-4 text-white hover:bg-red-600 bg-red-500 w-full md:w-auto transition ease-in-out duration-300" onClick={handleDeleteUser} disabled={isLoading}>
                  Удалить
                </button>
                <button onClick={() => setTotalEditState(false)} type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 w-full md:w-auto hover:text-white bg-white hover:bg-button transition ease-in-out duration-300" disabled={isLoading}>
                  Отмена
                </button>
              </>
            )}

            {/* Секция полномочий для staff пользователей */}
            {isStaffUser && (
              <>
                <hr className="my-4" />
                <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov w-full md:w-auto transition ease-in-out duration-300" onClick={() => setPermissionsEditState(!permissionsEditState)}>
                  {permissionsEditState ? 'Скрыть полномочия' : 'Полномочия'}
                </button>

                {permissionsEditState && (
                  <div className="mt-4 p-4 border rounded-lg bg-gray-50 select-none">
                    <PermissionsCheckboxList form={form} name="permissions" userPermissions={userPermissionsData || []} disabled={isLoading} hideEmployersLimitsOption={hideEmployersLimitsPermissionOption} />

                    {showQuotaBlock && (
                      <div className="mt-0 pt-0 rounded-md border border-gray-200 bg-white p-4 space-y-3">
                        <div>
                          {/* {!isSuperuser && <p className={`text-xs mt-1 ${isQuotaExceeded ? 'text-red-500' : 'text-gray-500'}`}>Доступно для назначения: {formatQuotaValue(editableQuotaLimit)}</p>} */}
                          {isQuotaBlockedByMissingData && <p className="text-xs text-red-500 mt-1">Не удалось загрузить исходный лимит. Сохранение лимита заблокировано.</p>}
                        </div>

                        <p className="text-sm font-medium text-gray-400">Лимиты</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm pt-0">
                          <div className="rounded-md border border-gray-200 p-3 pt-0">
                            <p className="font-medium text-gray-800">Ваш</p>
                            <p className="text-gray-600 mt-1">Лимит: {isSuperuser ? '∞' : formatQuotaValue(ownLimitsTotalDisplay)}</p>
                            {isSuperAdminAdmin && <p className={isQuotaExceeded ? 'text-red-500' : 'text-gray-600'}>Доступно: {isSuperuser ? '∞' : formatQuotaValue(ownLimitsAvailableDisplay)}</p>}
                          </div>
                          <div className="rounded-md border border-gray-200 p-3 pt-0">
                            <p className="font-medium text-gray-800">{getUserByIdData?.full_name || 'Лимит пользователя'}</p>
                            <p className="text-gray-600 mt-1">Назначенный лимит: {formatQuotaValue(targetLimitsTotalDisplay)}</p>
                            {/* <p className={isQuotaExceeded ? 'text-red-500' : 'text-gray-600'}>Доступно: {formatQuotaValue(targetLimitsAvailableDisplay)}</p> */}
                          </div>
                        </div>

                        <p className="text-sm font-medium text-gray-400 ">Контрагенты</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="rounded-md border border-gray-200 p-3 pt-0">
                            <p className="font-medium text-gray-800">Ваши</p>
                            {isSuperAdminAdmin ? (
                              <>
                                <p className="text-gray-600 mt-1">Лимит: {isCurrentUserSuperuser ? '∞' : formatQuotaValue(ownContractorsTotalDisplay)}</p>
                                <p className="text-gray-600">Доступно: {isCurrentUserSuperuser ? '∞' : formatQuotaValue(ownContractorsAvailableDisplay)}</p>
                              </>
                            ) : (
                              <p className="text-gray-600 mt-1">Свободно: {formatQuotaValue(ownContractorsFreeDisplay)}</p>
                            )}
                          </div>
                          {/* <div className="rounded-md border border-gray-200 p-3">
                            <p className="font-medium text-gray-800">{getUserByIdData?.full_name || 'Пользователь'}</p>
                            <p className="text-gray-600 mt-1">Всего: {isTargetUserSuperuser ? '∞' : formatQuotaValue(targetContractorsTotalDisplay)}</p>
                            <p className="text-gray-600">Доступно: {isTargetUserSuperuser ? '∞' : formatQuotaValue(targetContractorsAvailableDisplay)}</p>
                          </div> */}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2  gap-3 text-sm">
                          <button
                            type="button"
                            className="text-center border rounded py-2 px-4 text-white bg-button hover:bg-button-hov transition ease-in-out duration-300 disabled:opacity-50"
                            onClick={() => setIsFreeEmployersModalOpen(true)}
                            disabled={isLoading || isEmployersActionsBlockedByMeData}>
                            Свободные контрагенты({freeEmployersCount})
                          </button>
                          <button
                            type="button"
                            className="text-center border rounded py-2 px-4 text-button border-button border-1 hover:text-white bg-white hover:bg-button transition ease-in-out duration-300"
                            onClick={() => setIsAttachedEmployersModalOpen(true)}
                            disabled={isLoading || isEmployersActionsBlockedByMeData}>
                            Прикрепленные контрагенты ({attachedEmployersCount})
                          </button>
                          <button
                            type="button"
                            className="md:col-span-2 w-full text-center border rounded py-2 px-4 text-gray-700 border-gray-300 bg-gray-100 hover:bg-gray-200 hover:text-gray-900 transition ease-in-out duration-300 disabled:opacity-50"
                            onClick={() => {
                              dispatch(getUserEmployersDistribution(userId));
                              setIsDistributionModalOpen(true);
                            }}
                            disabled={isLoading}>
                            Подробнее
                          </button>
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-800">
                          <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500" disabled={isLoading} {...form.register('auto_employers')} />
                          Автораспределение контрагентов
                        </label>

                        <input
                          type="number"
                          placeholder="Введите лимит контрагентов"
                          disabled={isLoading || isQuotaBlockedByMissingData}
                          className={`w-full block rounded-md border border-1 border-gray-300 focus:border-button px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none sm:text-sm sm:leading-6 ${form.formState.errors?.quota?.message ? 'border-red-300 focus:border-red-500 placeholder:text-red-400' : ''}`}
                          {...quotaRegister}
                          onBlur={(e) => {
                            quotaRegister.onBlur(e);
                            handleQuotaBlur();
                          }}
                        />
                        {form.formState.errors?.quota?.message && <div className="text-sm text-red-400 mt-1">{form.formState.errors.quota.message}</div>}
                      </div>
                    )}

                    <div className="flex gap-3 mt-4">
                      <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov transition ease-in-out duration-300" onClick={form.handleSubmit(onSavePermissions)} disabled={isLoading}>
                        Сохранить полномочия
                      </button>
                      <button type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 hover:text-white bg-white hover:bg-button transition ease-in-out duration-300" onClick={() => setPermissionsEditState(false)} disabled={isLoading}>
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Modal
        className="select-user-modal"
        style={{ height: 'calc(100vh - 40px)' }}
        title={``}
        centered
        destroyOnClose
        open={isFreeEmployersModalOpen}
        width={'100%'}
        onCancel={() => {
          setIsFreeEmployersModalOpen(false);
        }}
        footer={['']}>
        <SelectionPerformer
          mode="employers-free"
          title={`Свободные контрагенты (${freeEmployersCount})`}
          sliceName="employer"
          actionName="getEmployerList"
          fetchParams={isSuperAdminAdmin ? { responsible_is_null: true, has_access: false } : { responsible_id: currentUserId ?? -1 }}
          submitButtonText="Прикрепить"
          selectionVisual="check-green"
          onSubmitSelection={handleSaveEmployers}
          isSubmitLoading={isSuperAdminAdmin ? addUserEmployersLoading : transferUserEmployersLoading}
          onClose={() => {
            setIsFreeEmployersModalOpen(false);
          }}
        />
      </Modal>
      <Modal
        className="select-user-modal"
        style={{ height: 'calc(100vh - 40px)' }}
        title={``}
        centered
        destroyOnClose
        open={isAttachedEmployersModalOpen}
        width={'100%'}
        onCancel={() => {
          setIsAttachedEmployersModalOpen(false);
        }}
        footer={['']}>
        <SelectionPerformer
          mode="employers-attached"
          title={`Прикрепленные контрагенты (${attachedEmployersCount})`}
          sliceName="employer"
          actionName="getEmployerList"
          fetchParams={{ responsible_id: userId }}
          submitButtonText="Открепить"
          selectionVisual="minus-red"
          onSubmitSelection={handleRemoveEmployers}
          isSubmitLoading={isTargetUserCreatedBySuperuser ? removeUserEmployersLoading : transferUserEmployersLoading}
          onClose={() => {
            setIsAttachedEmployersModalOpen(false);
          }}
        />
      </Modal>
      <Modal
        className="select-user-modal"
        style={{ height: 'calc(100vh - 40px)' }}
        title={``}
        centered
        destroyOnClose
        open={isDistributionModalOpen}
        width={'100%'}
        onCancel={() => {
          setIsDistributionModalOpen(false);
        }}
        footer={['']}>
        <EmployersDistributionView
          data={employersDistributionData}
          loading={employersDistributionLoading}
          error={employersDistributionError}
          onRefreshDistribution={() => {
            dispatch(getUserEmployersDistribution(userId));
            fetchEmployersCounts();
          }}
        />
      </Modal>
    </div>
  );
}

export default EditUser;
